/**
 * A comment thread, on whatever it hangs off.
 *
 * `comment.page_id` has been in the schema and the sync protocol since the
 * first release; only tasks ever showed one, which made the wiki a shelf
 * rather than a place people talk. The thread is the same thread either way,
 * so it is the same component — the target is one field.
 *
 * A comment can be rewritten by whoever wrote it, which is the same affordance
 * a chat message has had and the same `edited_at` behind it: stamped by the
 * server, never claimed by a client. The thread is where a decision gets
 * recorded, and a typo in the sentence that records it had, until now, exactly
 * two remedies — delete and say it again, or leave it wrong.
 */
import { useEffect, useRef, useState } from 'react';
import { useT } from '../../kernel/i18n/i18n';
import { relativeTime } from '../../kernel/design-system/format';
import { comment as postComment, remove, update } from '../../kernel/sync/mutations';
import { list, useQuery } from '../../kernel/sync/store';
import { useCanWrite, useMe, useMemberMap, useRunsWorkspace } from '../../kernel/identity/session';
import { anchorLabel, findAnchor, isPin, passageOf, textAnchor, type Anchor, type Comment, type Pin } from '@kolibri/shared';
import { Markdown, MarkdownEditor } from '../pages/Markdown';
import { Button } from '../../kernel/design-system/ui/button';
import { Textarea } from '../../kernel/design-system/ui/field';
import { Chip } from '../../kernel/design-system/ui/chip';
import { Avatar, Icon, useConfirm } from '../../kernel/design-system/ui';
import { Reactions, ReactionPicker } from './reactions';
import type { AttachTo } from '../../kernel/files/uploads';
import { useMinute } from '../../kernel/design-system/minute';

/** Exactly one of the two, which is also how the row is stored. */
export type CommentTarget = { task_id: string; page_id?: never } | { page_id: string; task_id?: never };

export function Comments({ target, empty, anchor, onAnchorDone, source, active, onPick, pinLabel, suggesting = false }: {
  target: CommentTarget;
  empty?: string;
  /**
   * What the next comment is about: a passage, set by selecting text on a page,
   * or a pin, set by clicking a board on a canvas.
   */
  anchor?: Anchor | Pin | null;
  onAnchorDone?: () => void;
  /** The text the anchors are expressed against, for showing what is orphaned. */
  source?: string;
  active?: string | null;
  onPick?: (id: string) => void;
  /** How a pin on a canvas is named — the board it is on. Only a canvas passes it. */
  pinLabel?: (pin: Pin) => string;
  /**
   * The next comment is a suggestion for `anchor`'s passage: the composer asks
   * what it should say instead, and the words of the comment become optional.
   */
  suggesting?: boolean;
}) {
  useMinute(); // re-reads the ages below once a minute — `design-system/minute.ts`
  const t = useT();
  const me = useMe();
  const canWrite = useCanWrite();
  const members = useMemberMap();
  /**
   * Whether this person may take down a note left from outside.
   *
   * A note through a public share link has no account behind it, so "your own"
   * names nobody — and the server's answer is not "then anybody": it may be
   * rewritten by no one and deleted by an admin or owner, who can also revoke
   * the link it came through. That rule needs a button, or it is a rule only
   * reachable with `curl`.
   *
   * Asked through the hook rather than re-spelled as `role === …`, which is
   * what `useRunsWorkspace`'s own note is about.
   */
  const runsThis = useRunsWorkspace();
  const { confirm, dialog } = useConfirm();
  const [draft, setDraft] = useState('');
  /** What a suggestion would put in place of its passage. */
  const [replacement, setReplacement] = useState('');
  /** Settled comments are folded away until somebody asks to see them. */
  const [showSettled, setShowSettled] = useState(false);
  const proposing = suggesting && !!passageOf(anchor);
  /** Which comment is open for editing, and the text as it stands. */
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  const editor = useRef<HTMLDivElement>(null);

  // A selection jumps to the composer: the thing somebody does after choosing a
  // sentence is type about it, and hunting for the box is a step in the way.
  useEffect(() => {
    if (!anchor) return;
    // A suggestion starts from the words it replaces: changing a sentence is
    // easier than retyping it.
    setReplacement(passageOf(anchor)?.quote ?? '');
    editor.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    editor.current?.querySelector('textarea')?.focus();
  }, [anchor]);

  const key = target.task_id ?? target.page_id;
  /*
   * An image pasted into a comment hangs off the thing being discussed, not off
   * the comment — which is what this has always done, now said in the one
   * spelling every upload uses. `comment` is a subject too, and it is the one
   * the *sent* comment's own attachments carry; a paste happens before there is
   * a comment to carry anything.
   */
  const attachTo: AttachTo = target.task_id
    ? { kind: 'task', id: target.task_id }
    : { kind: 'page', id: target.page_id! };

  // An open editor belongs to the thread it was opened in. The draft below
  // deliberately survives a trip to another page — somebody who typed it meant
  // to — but an edit box pointing at a comment that is no longer on screen is
  // just a box that reopens on the way back.
  useEffect(() => setEditing(null), [key]);

  const comments = useQuery(
    () => list('comment', (entry) => (target.task_id ? entry.task_id === target.task_id : entry.page_id === target.page_id))
      .sort((a, b) => a.created_at - b.created_at),
    [key],
  );

  const send = () => {
    const body = draft.trim();
    if (proposing) {
      // A suggestion that says nothing new is not one.
      if (replacement === passageOf(anchor)?.quote) return;
      postComment(target, body, me, anchor ?? null, replacement);
    } else {
      if (!body) return;
      postComment(target, body, me, anchor ?? null);
    }
    setDraft('');
    setReplacement('');
    onAnchorDone?.();
  };

  /**
   * Whether this thread is one that gets settled: a page's, where comments are
   * review — a passage to fix, a suggestion to take. A task's thread is its
   * log, and folding away half of it because somebody ticked "resolved" would
   * hide the history the task is for.
   */
  const settleable = !!target.page_id;

  /** Settle a comment, or open it again. The server stamps who and when. */
  const settle = (entry: Comment, resolution: Comment['resolution']) => update('comment', entry.id, { resolution });
  const settledCount = comments.filter((entry) => entry.resolution).length;

  /**
   * Commit an edit, or quietly drop one that changed nothing.
   *
   * An emptied body is *not* a delete. Deleting is a confirmed action one
   * button along, and clearing a field by accident — select all, type, undo
   * badly — should not be the unconfirmed way to reach it.
   */
  const saveEdit = () => {
    if (!editing) return;
    const body = editing.body.trim();
    const before = comments.find((entry) => entry.id === editing.id)?.body ?? '';
    if (body && body !== before) update('comment', editing.id, { body });
    setEditing(null);
  };

  return (
    <>
      {comments.length === 0 && empty && <p className="text-muted text-[12.5px]">{empty}</p>}
      {settleable && settledCount > 0 && (
        <button type="button" className="comment-settled-toggle" aria-expanded={showSettled} onClick={() => setShowSettled(!showSettled)}>
          <Icon name={showSettled ? 'chevronDown' : 'chevronRight'} size={12} />
          {t(showSettled ? 'comment.hideSettled' : 'comment.showSettled', { count: settledCount })}
        </button>
      )}
      {comments.filter((entry) => !settleable || showSettled || !entry.resolution || entry.id === active).map((entry) => {
        const author = members.get(entry.author_id);
        const quoted = textAnchor(entry.anchor);
        const pin = isPin(entry.anchor) ? entry.anchor : null;
        // Read defensively: a row synced before these columns existed has
        // neither, and is an open, ordinary comment.
        const suggestion = typeof entry.suggestion === 'string' ? entry.suggestion : null;
        const passage = passageOf(entry.anchor);
        const resolution = entry.resolution ?? null;
        const settler = entry.resolved_by ? members.get(entry.resolved_by) : undefined;
        // A note left through a public link has no account behind it. The name
        // is whatever was typed into a box, so it is shown as exactly that
        // rather than sitting in the row looking like a colleague.
        const guest = !entry.author_id && entry.guest_name !== undefined;
        return (
          <div className={`comment${resolution ? ' is-settled' : ''}`} key={entry.id}>
            <Avatar user={guest ? undefined : author} size={26} />
            <div className="body">
              <div className="flex items-center gap-1.5">
                <span className="who">
                  {guest ? (entry.guest_name || t('comment.anonymous')) : (author?.name ?? t('common.someone'))}
                </span>
                {guest && <Chip>{t('comment.fromOutside')}</Chip>}
                <span className="when">{relativeTime(entry.created_at)}</span>
                {/* Only that it was, not when: a second timestamp beside the
                    first reads as two comments, and what anybody actually
                    wants to know is whether these are still the words that
                    were replied to. */}
                {entry.edited_at && <span className="when">· {t('comment.edited')}</span>}
                {(entry.author_id === me || (guest && runsThis)) && (
                  <span className="flex items-center gap-0.5" style={{ marginInlineStart: 'auto' }}>
                    {/* No pencil on a guest's note, whoever is looking: there is
                        no author to be, and putting words in a stranger's mouth
                        under the name they typed is worse than leaving it. */}
                    {entry.author_id === me && (
                      <Button variant="ghost" size="sm"
                        aria-label={t('comment.edit')}
                        title={t('comment.edit')}
                        onClick={() => setEditing({ id: entry.id, body: entry.body })}
                      >
                        <Icon name="pencil" size={13} />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm"
                      aria-label={t('task.deleteCommentLabel')}
                      onClick={async () => {
                        if (await confirm(t('task.deleteComment'))) remove('comment', entry.id);
                      }}
                    >
                      <Icon name="trash" size={13} />
                    </Button>
                  </span>
                )}
              </div>
              {quoted && (
                <button
                  className={`quoted${findAnchor(source ?? '', quoted) ? '' : ' orphan'}`}
                  onClick={() => onPick?.(entry.id)}
                  title={findAnchor(source ?? '', quoted) ? t('annotate.jump') : t('annotate.orphanHint')}
                >
                  <Icon name="link" size={11} /> {anchorLabel(quoted)}
                  {source !== undefined && !findAnchor(source, quoted) && (
                    <em> · {t('annotate.orphan')}</em>
                  )}
                </button>
              )}
              {pin && pinLabel && (
                <button className="quoted" onClick={() => onPick?.(entry.id)}>
                  <Icon name="pin" size={11} /> {pinLabel(pin)}
                </button>
              )}
              {suggestion !== null && passage && (
                <div className="suggestion-diff">
                  <span className="sr-only">{t('suggest.replace')}</span>
                  <del>{passage.quote}</del>
                  <span className="sr-only">{t('suggest.with')}</span>
                  <ins>{suggestion || t('suggest.removed')}</ins>
                </div>
              )}
              {resolution && (
                <p className="comment-settled">
                  <Icon name="check" size={12} />
                  {t(resolution === 'accepted' ? 'comment.accepted' : resolution === 'rejected' ? 'comment.rejected' : 'comment.resolved', {
                    name: settler?.name ?? t('common.someone'),
                    time: relativeTime(entry.resolved_at ?? entry.updated_at),
                  })}
                  {canWrite && resolution !== 'accepted' && (
                    <button type="button" className="comment-link" onClick={() => settle(entry, null)}>{t('comment.reopen')}</button>
                  )}
                </p>
              )}
              {settleable && !resolution && canWrite && !entry.parent_id && (
                <div className="comment-settle">
                  {suggestion !== null && passage ? (
                    <>
                      <Button size="sm" variant="primary" onClick={() => settle(entry, 'accepted')}>
                        <Icon name="check" size={13} /> {t('suggest.accept')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => settle(entry, 'rejected')}>{t('suggest.reject')}</Button>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => settle(entry, 'resolved')}>
                      <Icon name="check" size={13} /> {t('comment.resolve')}
                    </Button>
                  )}
                </div>
              )}
              {editing?.id === entry.id ? (
                <div className="mt-1.5">
                  {/* The same editor the composer uses, so an edit is typed
                      the way the comment was — `@` menus, attachments and all. */}
                  <MarkdownEditor
                    value={editing.body}
                    onChange={(body) => setEditing({ id: entry.id, body })}
                    minHeight={70}
                    autoFocus
                    attachTo={attachTo}
                    onSubmit={saveEdit}
                  />
                  <div className="flex items-center gap-2 mt-2" style={{ justifyContent: 'flex-end' }}>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>{t('action.cancel')}</Button>
                    <Button variant="primary" size="sm" disabled={!editing.body.trim()} onClick={saveEdit}>
                      <Icon name="check" size={14} /> {t('action.save')}
                    </Button>
                  </div>
                </div>
              ) : (
                <Markdown source={entry.body} />
              )}
              <Reactions kind="comment" id={entry.id} reactions={entry.reactions} canWrite={canWrite}>
                {canWrite && (
                  <ReactionPicker kind="comment" id={entry.id} reactions={entry.reactions} align="start">
                    <button className="reaction add" aria-label={t('task.react')} title={t('task.react')}>
                      <Icon name="plus" size={12} />
                    </button>
                  </ReactionPicker>
                )}
              </Reactions>
            </div>
          </div>
        );
      })}
      <div className="mt-2.5" ref={editor}>
        {anchor && (
          <div className="flex items-center gap-2 quoted-draft">
            <Icon name={proposing ? 'pencil' : 'link'} size={12} />
            <span className="flex-1 min-w-0 truncate">
              {proposing && <strong>{t('suggest.draftLabel')} </strong>}
              {isPin(anchor) && !proposing ? pinLabel?.(anchor) : anchorLabel(passageOf(anchor) ?? (anchor as Anchor))}
            </span>
            <Button variant="ghost" size="iconSm" aria-label={t('annotate.clear')} onClick={() => onAnchorDone?.()}>
              <Icon name="close" size={12} />
            </Button>
          </div>
        )}
        {proposing && (
          <label className="suggestion-field">
            <span>{t('suggest.replaceWith')}</span>
            <Textarea value={replacement} rows={3} onChange={(event) => setReplacement(event.target.value)} />
          </label>
        )}
        <MarkdownEditor
          value={draft}
          onChange={setDraft}
          minHeight={70}
          placeholder={proposing ? t('suggest.why') : t('task.commentPlaceholder')}
          attachTo={attachTo}
          onSubmit={send}
        />
        <div className="flex items-center gap-2 mt-2" style={{ justifyContent: 'flex-end' }}>
          <Button
            variant="primary" size="sm" onClick={send}
            disabled={proposing ? replacement === passageOf(anchor)?.quote : !draft.trim()}
          >
            <Icon name="send" size={14} /> {proposing ? t('suggest.send') : t('task.comment')}
          </Button>
        </div>
      </div>
      {dialog}
    </>
  );
}
